import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { AlertTriangle, Merge } from "lucide-react";
import { Modal } from "@/components/panel/ui";
import ProductPicker from "@/components/panel/pickers/ProductPicker";
import { Button, Select } from "@/components/ui-kit";

/** Singular/plural do que mudou de dono, para o aviso dizer o que aconteceu. */
const ROTULOS = {
  pos_sale_items: ["venda do balcão", "vendas do balcão"],
  order_items: ["item de pedido", "itens de pedido"],
  stock_movements: ["movimentação de estoque", "movimentações de estoque"],
  quote_items: ["item de orçamento", "itens de orçamento"],
  rentals: ["aluguel", "aluguéis"],
  service_orders: ["ordem de serviço", "ordens de serviço"],
  xml_aliases: ["apelido de XML", "apelidos de XML"],
  reviews: ["avaliação", "avaliações"],
};

/** "3 vendas do balcão, 5 movimentações de estoque e +2 no estoque" */
function resumo(moved = {}) {
  const partes = Object.entries(ROTULOS)
    .filter(([k]) => moved[k] > 0)
    .map(([k, [um, varios]]) => `${moved[k]} ${moved[k] === 1 ? um : varios}`);
  if (moved.stock > 0) partes.push(`+${moved.stock} no estoque`);

  return partes.length ? partes.join(", ") : "nada a mover";
}

/**
 * Vincular um cadastro duplicado ao cadastro certo, variação por variação.
 *
 * O caso é o da migração: o mesmo produto entrou três vezes — "ORTESE ... DIR.
 * P", "ORTESE ... DIR. G" e o cadastro bom, com P, M e G. Os duplicados não
 * podiam ser excluídos (têm venda e movimentação apontando para eles) nem
 * desativados sem prejuízo (o saldo real está partido entre eles), então a
 * saída era conviver com catálogo dobrado.
 *
 * Aqui o operador diz para onde vai CADA variação. Nada é adivinhado por nome
 * parecido: acertar em "DIR. P" → "Tam P" na maioria e errar calado no resto
 * mandaria venda de um tamanho para a linha de outro, e ninguém perceberia.
 */
export default function MergeDialog({ product, onClose, onDone }) {
  const [destino, setDestino] = useState(null);
  const [mapa, setMapa] = useState({});
  const [saving, setSaving] = useState(false);

  const variacoes = product.variants || [];
  const faltam = variacoes.filter((v) => !mapa[v.id]).length;

  const escolherDestino = ({ product: p }) => {
    if (p?.id === product.id) {
      toast.error("Escolha um produto diferente para receber o histórico.");
      return;
    }
    setDestino(p || null);
    setMapa({}); // outro destino, outras variações: o mapa anterior não vale mais
  };

  const confirmar = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/products/${product.id}/merge`, { map: mapa });
      toast.success(`Histórico movido para ${destino.name}`, { description: resumo(data.moved) });
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Vincular a outro produto" maxWidth="max-w-xl" testId="product-merge-dialog">
      <p className="text-sm text-slate-600">
        Manda o histórico de <b>{product.name}</b> para as variações de outro cadastro e <b>exclui este produto</b>.
        Venda, pedido, movimentação de estoque, orçamento e apelido de XML continuam existindo — passam a apontar
        para a variação escolhida. O saldo de cada variação é somado ao destino.
      </p>

      <div className="mt-4">
        <span className="block text-eyebrow uppercase tracking-wider text-slate-500 mb-1.5">Produto que fica</span>
        <ProductPicker
          productId={destino?.id || ""}
          selectedLabel={destino?.name || ""}
          onChange={escolherDestino}
          showVariant={false}
          placeholder="Buscar o cadastro correto..."
          testId="merge-target-picker"
        />
      </div>

      {destino && (
        <div className="mt-4">
          <span className="block text-eyebrow uppercase tracking-wider text-slate-500 mb-1.5">
            Para onde vai cada variação
          </span>
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg" data-testid="merge-map">
            {variacoes.map((v) => (
              <li key={v.id} className="flex items-center gap-3 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-900 truncate">{v.label}</p>
                  <p className="text-xs text-slate-400">
                    saldo <span className="font-mono">{v.stock}</span>
                    {v.barcode ? <> · <span className="font-mono">{v.barcode}</span></> : null}
                  </p>
                </div>
                <span className="text-slate-300 shrink-0" aria-hidden>→</span>
                <Select
                  value={mapa[v.id] || ""}
                  onChange={(e) => setMapa({ ...mapa, [v.id]: e.target.value })}
                  aria-label={`Variação de destino para ${v.label}`}
                  data-testid={`merge-target-${v.id}`}
                  className="w-44 shrink-0"
                >
                  <option value="">Escolha...</option>
                  {(destino.variants || []).map((d) => (
                    <option key={d.id} value={d.id}>{d.label} (saldo {d.stock})</option>
                  ))}
                </Select>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* O aviso fica ao lado do botão, e não no topo: é aqui que a pessoa
          decide. Exclusão de cadastro não tem desfazer. */}
      <div className="flex gap-2 mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
        <AlertTriangle size={15} className="text-amber-600 shrink-0 mt-0.5" aria-hidden />
        <p className="text-xs text-amber-900 leading-relaxed">
          Confira os tamanhos antes de confirmar: depois de mover, <b>{product.name}</b> deixa de existir e o
          histórico já estará no outro cadastro. Não dá para desfazer.
        </p>
      </div>

      <div className="flex gap-3 mt-5">
        <Button variant="secondary" block onClick={onClose}>Cancelar</Button>
        <Button block icon={Merge} loading={saving} disabled={!destino || faltam > 0}
          onClick={confirmar} data-testid="merge-confirm">
          {faltam > 0 ? `Falta escolher ${faltam}` : "Mover e excluir duplicado"}
        </Button>
      </div>
    </Modal>
  );
}
